import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  PageHeader,
  Table,
  Drawer,
  SearchBar,
  BadgePill,
  EmptyState,
} from '@/components/campus';
import {
  UserPlus,
  Mail,
  User,
  Shield,
  Clock,
  Calendar,
  MoreHorizontal,
  Eye,
  Loader2,
  UserCog,
  UserMinus,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role configuration
const roleConfig = {
  owner: { label: 'Owner', color: 'bg-electric-ice-blue text-white', variant: 'primary' },
  admin: { label: 'Admin', color: 'bg-cool-mint text-obsidian', variant: 'success' },
  event_manager: { label: 'Event Manager', color: 'bg-soft-amber text-obsidian', variant: 'warning' },
  door_staff: { label: 'Door Staff', color: 'bg-soft-silver text-obsidian', variant: 'default' },
  member: { label: 'Member', color: 'bg-graphite text-cloud-white', variant: 'default' },
};

// Status configuration
const statusConfig = {
  active: { label: 'Active', variant: 'success' },
  invited: { label: 'Invited', variant: 'warning' },
  deactivated: { label: 'Deactivated', variant: 'error' },
};

// Role options for dropdown (excluding owner - can only be transferred)
const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'event_manager', label: 'Event Manager' },
  { value: 'door_staff', label: 'Door Staff' },
  { value: 'member', label: 'Member' },
];

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ==================== INVITE MEMBER FORM ====================
const InviteMemberForm = ({ onSubmit, onCancel, isLoading }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [displayName, setDisplayName] = useState('');
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!role) {
      newErrors.role = 'Role is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({ email, role, display_name: displayName || null });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Email */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Email Address *</Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors(prev => ({ ...prev, email: null }));
          }}
          placeholder="colleague@university.edu"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="invite-email-input"
        />
        {errors.email && <p className="text-signal-red text-xs">{errors.email}</p>}
      </div>

      {/* Display Name (optional) */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Display Name</Label>
        <Input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="John Smith (optional)"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="invite-name-input"
        />
      </div>

      {/* Role */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Role *</Label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            {roleOptions.map(opt => (
              <SelectItem 
                key={opt.value} 
                value={opt.value}
                className="text-cloud-white hover:bg-white/[0.05]"
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.role && <p className="text-signal-red text-xs">{errors.role}</p>}
      </div>

      {/* Role Description */}
      <div className="p-3 bg-obsidian/50 rounded-lg border border-white/[0.06] text-sm text-soft-silver">
        {role === 'admin' && 'Can manage events, guests, members, and organization settings.'}
        {role === 'event_manager' && 'Can create and manage events, approve guests.'}
        {role === 'door_staff' && 'Can scan QR codes and check in guests at events.'}
        {role === 'member' && 'Basic access to view organization content.'}
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 border-white/[0.08] text-soft-silver hover:text-cloud-white"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
          disabled={isLoading}
          data-testid="invite-submit-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Inviting...
            </>
          ) : 'Send Invite'}
        </Button>
      </div>
    </form>
  );
};

// ==================== MEMBER DETAIL DRAWER ====================
const MemberDetailDrawer = ({ 
  member, 
  isOpen, 
  onClose, 
  onUpdateRole,
  onDeactivate,
  onReactivate,
  onRemove,
  canManage,
  currentUserRole,
  isProcessing 
}) => {
  const [newRole, setNewRole] = useState(member?.role || '');
  
  useEffect(() => {
    if (member) {
      setNewRole(member.role);
    }
  }, [member]);

  if (!member) return null;

  const isOwner = member.role === 'owner';
  const isCurrentUserOwner = currentUserRole === 'owner';
  const canChangeRole = canManage && !isOwner && member.status === 'active';
  const canDeactivate = canManage && !isOwner && member.status === 'active';
  const canReactivate = canManage && member.status === 'deactivated';
  const canRemove = canManage && !isOwner;

  const handleRoleChange = () => {
    if (newRole !== member.role) {
      onUpdateRole(member.user_id, newRole);
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Member Details" width="lg">
      <div className="space-y-6">
        {/* Member Header */}
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
            <User className="w-8 h-8 text-electric-ice-blue" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-cloud-white">
              {member.display_name || member.email?.split('@')[0]}
            </h3>
            <p className="text-soft-silver mt-1">{member.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <BadgePill variant={roleConfig[member.role]?.variant || 'default'}>
                {roleConfig[member.role]?.label || member.role}
              </BadgePill>
              <BadgePill variant={statusConfig[member.status]?.variant || 'default'}>
                {statusConfig[member.status]?.label || member.status}
              </BadgePill>
            </div>
          </div>
        </div>

        {/* Member Info */}
        <div className="space-y-3">
          <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-electric-ice-blue" />
              <div>
                <p className="text-sm text-muted-text">Joined</p>
                <p className="text-cloud-white font-mono">{formatDate(member.created_at)}</p>
              </div>
            </div>
          </div>

          {member.invited_by_name && (
            <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
              <div className="flex items-center gap-3">
                <UserPlus className="w-5 h-5 text-cool-mint" />
                <div>
                  <p className="text-sm text-muted-text">Invited By</p>
                  <p className="text-cloud-white">{member.invited_by_name}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Role Change Section */}
        {canChangeRole && (
          <div className="space-y-3">
            <Label className="text-soft-silver">Change Role</Label>
            <div className="flex gap-2">
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="flex-1 bg-obsidian border-white/[0.08] text-cloud-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-graphite border-white/[0.08]">
                  {roleOptions.map(opt => (
                    <SelectItem 
                      key={opt.value} 
                      value={opt.value}
                      className="text-cloud-white hover:bg-white/[0.05]"
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleRoleChange}
                disabled={newRole === member.role || isProcessing}
                className="bg-electric-ice-blue/10 text-electric-ice-blue border border-electric-ice-blue/20"
                data-testid="change-role-btn"
              >
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
              </Button>
            </div>
          </div>
        )}

        {/* Owner Warning */}
        {isOwner && (
          <div className="flex items-center gap-2 p-3 bg-electric-ice-blue/10 rounded-lg border border-electric-ice-blue/20">
            <Shield className="w-5 h-5 text-electric-ice-blue" />
            <span className="text-electric-ice-blue text-sm">Organization Owner - Cannot be modified</span>
          </div>
        )}

        {/* Actions */}
        {canManage && !isOwner && (
          <div className="space-y-3 pt-4 border-t border-white/[0.06]">
            {canDeactivate && (
              <Button
                onClick={() => onDeactivate(member.user_id)}
                variant="outline"
                className="w-full border-soft-amber/30 text-soft-amber hover:bg-soft-amber/10"
                disabled={isProcessing}
              >
                <UserMinus className="w-4 h-4 mr-2" />
                Deactivate Member
              </Button>
            )}
            
            {canReactivate && (
              <Button
                onClick={() => onReactivate(member.user_id)}
                className="w-full bg-cool-mint/10 text-cool-mint border border-cool-mint/20"
                disabled={isProcessing}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Reactivate Member
              </Button>
            )}
            
            {canRemove && (
              <Button
                onClick={() => {
                  if (window.confirm(`Are you sure you want to remove ${member.display_name || member.email}? This action cannot be undone.`)) {
                    onRemove(member.user_id);
                  }
                }}
                variant="outline"
                className="w-full border-signal-red/30 text-signal-red hover:bg-signal-red/10"
                disabled={isProcessing}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Remove from Organization
              </Button>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

// ==================== MAIN MEMBERS PAGE ====================
export const Members = () => {
  const { organization, session, orgRole } = useAuth();
  const { hasOrgRole } = usePermissions();
  
  // Check permissions
  const canManage = hasOrgRole(['owner', 'admin']);
  const canView = hasOrgRole(['owner', 'admin', 'event_manager']);

  // State
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pageSize: 50 });

  // Fetch members
  const fetchMembers = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/members?organization_id=${organization.id}&page_size=100`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
        setPagination(prev => ({ ...prev, total: data.total || 0 }));
      }
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Filter members by search
  const filteredMembers = members.filter(member => 
    member.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Invite member
  const handleInvite = async (data) => {
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/members?organization_id=${organization.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        toast.success('Invitation sent successfully');
        setIsInviteOpen(false);
        fetchMembers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update member role
  const handleUpdateRole = async (userId, newRole) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/members/${userId}?organization_id=${organization.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (res.ok) {
        toast.success('Role updated successfully');
        fetchMembers();
        // Update selected member if viewing
        if (selectedMember?.user_id === userId) {
          setSelectedMember(prev => ({ ...prev, role: newRole }));
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setIsProcessing(false);
    }
  };

  // Deactivate member
  const handleDeactivate = async (userId) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/members/${userId}?organization_id=${organization.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ status: 'deactivated' }),
      });

      if (res.ok) {
        toast.success('Member deactivated');
        setIsDetailOpen(false);
        fetchMembers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to deactivate member');
      }
    } catch (error) {
      toast.error('Failed to deactivate member');
    } finally {
      setIsProcessing(false);
    }
  };

  // Reactivate member
  const handleReactivate = async (userId) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/members/${userId}?organization_id=${organization.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ status: 'active' }),
      });

      if (res.ok) {
        toast.success('Member reactivated');
        setIsDetailOpen(false);
        fetchMembers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reactivate member');
      }
    } catch (error) {
      toast.error('Failed to reactivate member');
    } finally {
      setIsProcessing(false);
    }
  };

  // Remove member
  const handleRemove = async (userId) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/members/${userId}?organization_id=${organization.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Member removed');
        setIsDetailOpen(false);
        fetchMembers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to remove member');
      }
    } catch (error) {
      toast.error('Failed to remove member');
    } finally {
      setIsProcessing(false);
    }
  };

  // View member detail
  const handleViewMember = (member) => {
    setSelectedMember(member);
    setIsDetailOpen(true);
  };

  // Table columns
  const columns = [
    {
      key: 'display_name',
      label: 'Member',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
            <User className="w-5 h-5 text-electric-ice-blue" />
          </div>
          <div>
            <p className="font-medium text-cloud-white">{row.display_name || row.email?.split('@')[0]}</p>
            <p className="text-xs text-muted-text">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      align: 'center',
      render: (value) => (
        <BadgePill variant={roleConfig[value]?.variant || 'default'}>
          {roleConfig[value]?.label || value}
        </BadgePill>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      render: (value) => (
        <BadgePill variant={statusConfig[value]?.variant || 'default'}>
          {statusConfig[value]?.label || value}
        </BadgePill>
      ),
    },
    {
      key: 'created_at',
      label: 'Joined',
      render: (value) => (
        <span className="text-soft-silver font-mono text-sm">{formatDate(value)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      sortable: false,
      render: (_, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4 text-muted-text" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
            <DropdownMenuItem 
              onClick={() => handleViewMember(row)}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            {canManage && row.role !== 'owner' && (
              <>
                <DropdownMenuSeparator className="bg-white/[0.08]" />
                <DropdownMenuItem 
                  onClick={() => handleViewMember(row)}
                  className="text-soft-silver hover:bg-white/[0.05]"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Manage Role
                </DropdownMenuItem>
                {row.status === 'active' && (
                  <DropdownMenuItem 
                    onClick={() => handleDeactivate(row.user_id)}
                    className="text-soft-amber hover:bg-white/[0.05]"
                  >
                    <UserMinus className="w-4 h-4 mr-2" />
                    Deactivate
                  </DropdownMenuItem>
                )}
                {row.status === 'deactivated' && (
                  <DropdownMenuItem 
                    onClick={() => handleReactivate(row.user_id)}
                    className="text-cool-mint hover:bg-white/[0.05]"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Reactivate
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="members-page">
      {/* Page Header */}
      <PageHeader
        title="Members"
        subtitle={`${pagination.total} members`}
        actions={
          canManage && (
            <Button
              onClick={() => setIsInviteOpen(true)}
              className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
              data-testid="invite-member-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          )
        }
      />

      {/* Search */}
      <SearchBar
        placeholder="Search by name or email..."
        value={searchQuery}
        onChange={setSearchQuery}
      />

      {/* Members Table */}
      <Table
        columns={columns}
        data={filteredMembers}
        emptyState={
          <div className="py-12">
            <EmptyState
              icon="users"
              title="No members found"
              description={searchQuery 
                ? "Try adjusting your search" 
                : "Invite team members to start collaborating."
              }
              actionLabel={canManage && !searchQuery ? "Invite Member" : undefined}
              onAction={canManage && !searchQuery ? () => setIsInviteOpen(true) : undefined}
            />
          </div>
        }
      />

      {/* Invite Member Drawer */}
      <Drawer
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        title="Invite Member"
        width="md"
      >
        <InviteMemberForm
          onSubmit={handleInvite}
          onCancel={() => setIsInviteOpen(false)}
          isLoading={isSubmitting}
        />
      </Drawer>

      {/* Member Detail Drawer */}
      <MemberDetailDrawer
        member={selectedMember}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onUpdateRole={handleUpdateRole}
        onDeactivate={handleDeactivate}
        onReactivate={handleReactivate}
        onRemove={handleRemove}
        canManage={canManage}
        currentUserRole={orgRole}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export default Members;
